import { supabase } from '../lib/supabaseClient';

interface KnowledgeDocument {
  name: string;
  content: string;
  size: number;
}

/**
 * Fetch all documents from the noorai-docs bucket
 */
export async function fetchKnowledgeBaseDocs(): Promise<KnowledgeDocument[]> {
  try {
    console.log('🔍 [KNOWLEDGE BASE] Fetching documents from noorcad-docs bucket...');
    
    // List all files in the noorcad-docs bucket
    const { data: files, error: listError } = await supabase
      .storage
      .from('noorai-docs')
      .list('', {
        limit: 100,
        sortBy: { column: 'name', order: 'asc' }
      });

    if (listError) {
      console.error('❌ [KNOWLEDGE BASE] Error listing knowledge base files:', listError);
      console.error('❌ [KNOWLEDGE BASE] Error message:', listError.message);
      console.error('❌ [KNOWLEDGE BASE] This might be due to missing RLS policies on the storage bucket');
      return [];
    }

    if (!files || files.length === 0) {
      console.warn('⚠️ [KNOWLEDGE BASE] No documents found in noorcad-docs bucket');
      return [];
    }

    console.log(`📁 [KNOWLEDGE BASE] Found ${files.length} file(s) in noorcad-docs:`, files.map(f => f.name));

    // Download and process each file
    const documents: KnowledgeDocument[] = [];
    
    for (const file of files) {
      try {
        // Get public URL for the file (works for public buckets)
        const { data: urlData } = supabase
          .storage
          .from('noorai-docs')
          .getPublicUrl(file.name);

        if (!urlData?.publicUrl) {
          console.error(`Failed to get public URL for ${file.name}`);
          continue;
        }

        console.log(`📥 [KNOWLEDGE BASE] Downloading: ${file.name} from ${urlData.publicUrl.substring(0, 80)}...`);

        // Fetch the file using the public URL
        const response = await fetch(urlData.publicUrl);
        
        if (!response.ok) {
          console.error(`Error fetching ${file.name}: ${response.status} ${response.statusText}`);
          continue;
        }

        // Convert to text
        const text = await response.text();
        
        documents.push({
          name: file.name,
          content: text,
          size: file.metadata?.size || 0
        });

        console.log(`✅ [KNOWLEDGE BASE] Loaded: ${file.name} (${text.length} chars, ${Math.round((file.metadata?.size || 0) / 1024)}KB)`);
      } catch (error) {
        console.error(`Error processing ${file.name}:`, error);
      }
    }

    const totalSize = documents.reduce((sum, doc) => sum + doc.content.length, 0);
    console.log(`✅ [KNOWLEDGE BASE] Successfully loaded ${documents.length} document(s), Total: ${Math.round(totalSize / 1024)}KB`);
    
    return documents;
  } catch (error) {
    console.error('❌ [KNOWLEDGE BASE] Error fetching knowledge base:', error);
    return [];
  }
}

/**
 * Build enhanced knowledge context from documents
 * Returns a formatted string to append to system prompts
 */
export async function getKnowledgeContext(): Promise<string> {
  const docs = await fetchKnowledgeBaseDocs();
  
  if (docs.length === 0) {
    console.warn('⚠️ [KNOWLEDGE BASE] No documents available for context');
    return '';
  }

  console.log(`📚 [KNOWLEDGE BASE] Building context from ${docs.length} document(s)`);

  // Build context string with document summaries
  let context = '\n\n=== KNOWLEDGE BASE REFERENCE DOCUMENTS ===\n\n';
  context += 'You have access to the following reference materials. Use these to provide accurate, evidence-based answers:\n\n';

  docs.forEach((doc, index) => {
    // Truncate very long documents to avoid token limits
    const maxChars = 5000; // Limit per document
    const wasTruncated = doc.content.length > maxChars;
    const truncatedContent = wasTruncated 
      ? doc.content.substring(0, maxChars) + '\n\n[... document truncated ...]'
      : doc.content;

    if (wasTruncated) {
      console.log(`📄 [KNOWLEDGE BASE] Document ${index + 1}: "${doc.name}" - ${doc.content.length} chars (truncated to ${maxChars})`);
    } else {
      console.log(`📄 [KNOWLEDGE BASE] Document ${index + 1}: "${doc.name}" - ${doc.content.length} chars (full)`);
    }

    context += `--- Document ${index + 1}: ${doc.name} ---\n`;
    context += `${truncatedContent}\n\n`;
  });

  context += '=== END KNOWLEDGE BASE ===\n\n';
  context += 'When referencing these documents, cite them by name (e.g., "According to [document name]...").\n';

  const contextSize = context.length;
  console.log(`✅ [KNOWLEDGE BASE] Context built: ${Math.round(contextSize / 1024)}KB, ~${Math.round(contextSize / 4)} tokens`);

  return context;
}

/**
 * Cache for knowledge base to avoid repeated downloads
 */
let knowledgeCache: { context: string; timestamp: number } | null = null;
const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes

export async function getCachedKnowledgeContext(): Promise<string> {
  const now = Date.now();
  
  // Return cached if fresh
  if (knowledgeCache && (now - knowledgeCache.timestamp) < CACHE_DURATION) {
    const cacheAge = Math.round((now - knowledgeCache.timestamp) / 1000);
    const cacheSize = Math.round(knowledgeCache.context.length / 1024);
    console.log(`💾 [KNOWLEDGE BASE] Using cached context (${cacheAge}s old, ${cacheSize}KB)`);
    return knowledgeCache.context;
  }

  // Fetch fresh knowledge base
  if (knowledgeCache) {
    const cacheAge = Math.round((now - knowledgeCache.timestamp) / 1000);
    console.log(`� [KNOWLEDGE BASE] Cache expired (${cacheAge}s old), fetching fresh data...`);
  } else {
    console.log('🔄 [KNOWLEDGE BASE] No cache available, fetching fresh data...');
  }
  
  const context = await getKnowledgeContext();
  
  // Update cache
  knowledgeCache = {
    context,
    timestamp: now
  };

  console.log(`💾 [KNOWLEDGE BASE] Cache updated (valid for ${CACHE_DURATION / 1000}s)`);

  return context;
}
